// Environment configuration
// STREAM_URL: Lambda Function URL for streaming responses (no 29s API Gateway cap).
// Fill in after first deploy: `aws cloudformation describe-stacks --query "Stacks[0].Outputs"`
// or check the CloudFormation console → Outputs tab → StreamEndpoint.
// Set REACT_APP_STREAM_URL in .env.* to override per environment at build time.
const config = {
  development: {
    API_BASE_URL: 'https://r5sz5cx2v6.execute-api.eu-west-2.amazonaws.com/dev',
    CHAT_ENDPOINT: '/chat',
    STREAM_URL: process.env.REACT_APP_STREAM_URL_DEV || 'https://mmfwed34xcdjz6wsxthapw44ma0wvogu.lambda-url.eu-west-2.on.aws/',
    COMPARE_URL: process.env.REACT_APP_COMPARE_URL_DEV || 'https://mmfwed34xcdjz6wsxthapw44ma0wvogu.lambda-url.eu-west-2.on.aws/',
  },
  test: {
    API_BASE_URL: 'https://ucq3s7ekl3.execute-api.eu-west-2.amazonaws.com/test',
    CHAT_ENDPOINT: '/chat',
    STREAM_URL: process.env.REACT_APP_STREAM_URL_TEST || null,
    COMPARE_URL: process.env.REACT_APP_COMPARE_URL_TEST || null,
  },
  staging: {
    API_BASE_URL: 'https://r12n1fmhjl.execute-api.eu-west-2.amazonaws.com/staging',
    CHAT_ENDPOINT: '/chat',
    STREAM_URL: process.env.REACT_APP_STREAM_URL_STAGING || null,
    COMPARE_URL: process.env.REACT_APP_COMPARE_URL_STAGING || null,
  },
  production: {
    API_BASE_URL: 'https://eh5k5iel07.execute-api.eu-west-2.amazonaws.com/prod',
    CHAT_ENDPOINT: '/chat',
    // Use Lambda Function URL to avoid API Gateway 29s timeout (504 on long catalog+LLM requests).
    STREAM_URL: process.env.REACT_APP_STREAM_URL_PROD || 'https://wdq57akhxcsvrlfliwldzdrjhm0qyhmc.lambda-url.eu-west-2.on.aws/',
    COMPARE_URL: process.env.REACT_APP_COMPARE_URL_PROD || 'https://wdq57akhxcsvrlfliwldzdrjhm0qyhmc.lambda-url.eu-west-2.on.aws/',
  }
};

// REACT_APP_ENV=test for test backend, =staging for staging, etc.
// When running on localhost, prefer development so Compare uses Stream URL (avoids 404 from wrong env).
const currentEnv =
  (typeof window !== 'undefined' && window.location?.hostname === 'localhost' && process.env.REACT_APP_ENV !== 'test' && process.env.REACT_APP_ENV !== 'staging' && process.env.REACT_APP_ENV !== 'prod')
    ? 'development'
    : (process.env.REACT_APP_ENV || process.env.NODE_ENV || 'development');

const resolvedConfig = config[currentEnv] || config.production;

// Always prefer the Lambda Function URL (STREAM_URL) when configured.
//
// API Gateway has a hard 29s timeout. A cold-start chat request doing
// catalog lookup + LLM generation can exceed that (we've logged 33s runs) —
// API Gateway then returns 504 and the user sees "Sorry, I encountered an
// error", even though the Lambda finishes fine. On refresh the Lambda is
// warm, the same query runs in ~20s and works — the classic "works after
// refresh" bug. The Function URL has no 29s cap (up to 15 min).
// This was previously localhost-only; deployed builds wrongly fell through
// to API Gateway. getCompareUrl() already routes via the Function URL.
export function getChatRequestUrl() {
  if (resolvedConfig.STREAM_URL)
    return resolvedConfig.STREAM_URL.replace(/\/$/, '');
  return `${resolvedConfig.API_BASE_URL}${(resolvedConfig['CHAT_ENDPOINT'] || '/chat')}`;
}

// Colour Analysis can be tested against its own dev Lambda while the rest of
// My App continues to use its existing chat backend. Set this only on the
// Colour Analysis dev project; production can use the normal chat endpoint.
export function getColourAnalysisRequestUrl() {
  // The public site must share the production chat service. A separate URL is
  // useful for the isolated study, but a stale override must not route public
  // visitors' photos to the development analysis service.
  if (typeof window !== 'undefined'
    && window.location.hostname === 'my-app-three-murex.vercel.app')
    return getChatRequestUrl();
  const override = process.env.REACT_APP_COLOUR_ANALYSIS_URL;
  if (override) return override.replace(/\/$/, '');
  if (process.env.REACT_APP_COLOUR_STUDY_ONLY === 'true')
    return config.development.STREAM_URL.replace(/\/$/, '');
  return getChatRequestUrl();
}

// Compare products: always use Function URL when set (sync path). Fallback to STREAM_URL (same Lambda) then API chat endpoint.
// Set REACT_APP_COMPARE_URL at build time to force Compare to use that URL in any environment (e.g. deployed app hitting wrong host).
export function getCompareUrl() {
  const override = typeof process !== 'undefined' && process.env.REACT_APP_COMPARE_URL;
  if (override) return process.env.REACT_APP_COMPARE_URL.replace(/\/$/, '');
  if (resolvedConfig.COMPARE_URL) return resolvedConfig.COMPARE_URL.replace(/\/$/, '');
  if (resolvedConfig.STREAM_URL) return resolvedConfig.STREAM_URL.replace(/\/$/, '');
  return `${resolvedConfig.API_BASE_URL}${(resolvedConfig['CHAT_ENDPOINT'] || '/chat')}`;
}

// Billing (Son:mi+ Stripe checkout / portal). Always API Gateway, never the
// streaming Function URL: these are plain request/response calls and the
// Function URL has no /billing routes at all.
export function getBillingUrl(path = '/checkout') {
  return `${resolvedConfig.API_BASE_URL}/billing${path}`;
}

export default resolvedConfig;
